/**
 * The tutorial's scripted hole (docs/HINTS-LAYOUT-TUTORIAL.md B4, B5): the
 * deal, and 28 steps in 9 lessons. Every action here is checked against the
 * real engine in tutorialGame.test.ts.
 */
import type { Action, HoleStack } from '../../engine';

export const TUTORIAL_SEED = 20261102;

export const STACK: HoleStack = {
  dealer: 1,
  grids: [
    [7, 11, 7, 9, 8, 4, 10, 12],
    [5, 9, 6, 11, 8, 3, 10, 9],
  ],
  discard: 11,
  draws: [1, 6, 12, 7, 5, -5, 3, 7, 9, 0, 2],
};

export type Spot = 'deck' | 'discard' | 'held' | 'grid' | 'ada' | `slot:${number}` | `col:${number}`;
export type Lesson = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface Step {
  id: string;
  lesson: Lesson;
  /** do: wait for the taught move(s). watch: Ada plays. say: auto-advance. tap: wait for Next. end: wait for Results. */
  kind: 'do' | 'watch' | 'say' | 'tap' | 'end';
  line: string;
  spot: Spot[];
  /** do: the only human actions allowed. */
  allow?: Action[];
  /** do: how many allowed actions complete the step (default 1). */
  need?: number;
  /** watch: Ada's actions, in order. */
  bot?: Action[];
  /** say: auto-advance after this many ms (default 2400). */
  ms?: number;
  /** say: advance when the hole-end sheet opens instead of on a timer (step 27). */
  untilSheet?: boolean;
}

export const LESSONS = 9;
export const SAY_MS = 2400;
export const WRONG_TAP_LINE = 'Tap the one that glows.';

const YOU = 0;
const ADA = 1;
const D = (p: number): Action => ({ type: 'drawDeck', player: p });
const T = (p: number): Action => ({ type: 'drawDiscard', player: p });
const S = (p: number, slot: number): Action => ({ type: 'swap', player: p, slot });
const X = (p: number): Action => ({ type: 'discardDrawn', player: p });
const F = (p: number, slot: number): Action => ({ type: 'flip', player: p, slot });
const I = (p: number, slot: number): Action => ({ type: 'flipInitial', player: p, slot });

export const STEPS: Step[] = [
  { id: 'tee', lesson: 1, kind: 'do', need: 2, spot: ['slot:2', 'slot:5'], allow: [I(YOU, 2), I(YOU, 5)], line: 'Flip two cards to start. Try these two.' },
  { id: 'adaTee', lesson: 1, kind: 'watch', spot: ['ada'], bot: [I(ADA, 0), I(ADA, 5)], line: 'Ada flips two of hers.' },
  { id: 'goal', lesson: 1, kind: 'tap', spot: ['grid'], line: 'Lowest score wins. Each card counts its number.' },
  { id: 'draw1', lesson: 2, kind: 'do', spot: ['deck'], allow: [D(YOU)], line: 'Your turn. Draw the top card of the deck.' },
  { id: 'swap1', lesson: 3, kind: 'do', spot: ['held', 'slot:1'], allow: [S(YOU, 1)], line: 'A 1 is a fine card. Swap it in here.' },
  { id: 'oldCard', lesson: 3, kind: 'say', spot: ['discard'], line: 'Your old card goes to the discard pile.' },
  { id: 'ada1', lesson: 3, kind: 'watch', spot: ['ada'], bot: [D(ADA), S(ADA, 1)], line: 'Ada’s turn. Watch her cards.' },
  { id: 'draw2', lesson: 4, kind: 'do', spot: ['deck'], allow: [D(YOU)], line: 'Draw again.' },
  { id: 'toss', lesson: 4, kind: 'do', spot: ['held', 'discard'], allow: [X(YOU)], line: 'A 12 hurts. Discard it.' },
  { id: 'flip', lesson: 4, kind: 'do', spot: ['slot:0'], allow: [F(YOU, 0)], line: 'Then flip one hidden card. This one.' },
  { id: 'ada2', lesson: 4, kind: 'watch', spot: ['ada', 'discard'], bot: [D(ADA), X(ADA), F(ADA, 4)], line: 'Ada’s turn.' },
  { id: 'take7', lesson: 5, kind: 'do', spot: ['discard', 'slot:2'], allow: [T(YOU)], line: 'Ada tossed a 7. You have a 7. Take it!' },
  { id: 'pair', lesson: 5, kind: 'do', spot: ['col:2'], allow: [S(YOU, 6)], line: 'Put it under your 7.' },
  { id: 'pairWhy', lesson: 5, kind: 'tap', spot: ['col:2'], line: 'A matching column scores zero.' },
  { id: 'ada3', lesson: 5, kind: 'watch', spot: ['ada'], bot: [D(ADA), S(ADA, 4)], line: 'Ada’s turn.' },
  { id: 'adaPair', lesson: 5, kind: 'say', ms: 2000, spot: ['ada'], line: 'Ada matched a column too.' },
  { id: 'draw4', lesson: 6, kind: 'do', spot: ['deck'], allow: [D(YOU)], line: 'Draw from the deck.' },
  { id: 'eureka', lesson: 6, kind: 'do', spot: ['held', 'slot:3'], allow: [S(YOU, 3)], line: 'Eureka! The −5 is the best card. Keep it.' },
  { id: 'ada4', lesson: 6, kind: 'watch', spot: ['ada'], bot: [D(ADA), S(ADA, 6)], line: 'Ada’s turn.' },
  { id: 'draw5', lesson: 7, kind: 'do', spot: ['deck'], allow: [D(YOU)], line: 'Draw again.' },
  { id: 'bonus', lesson: 7, kind: 'do', spot: ['held', 'col:0'], allow: [S(YOU, 4)], line: 'Another 7. Put it under your other 7.' },
  { id: 'bonusWhy', lesson: 7, kind: 'tap', spot: ['col:0', 'col:2'], line: 'Two matching columns of the same card: −10 more.' },
  { id: 'ada5', lesson: 7, kind: 'watch', spot: ['ada'], bot: [D(ADA), X(ADA), F(ADA, 2)], line: 'Ada’s turn.' },
  { id: 'draw6', lesson: 8, kind: 'do', spot: ['deck', 'slot:7'], allow: [D(YOU)], line: 'One card still hidden. Uncover it to go out.' },
  { id: 'out', lesson: 8, kind: 'do', spot: ['held', 'slot:7'], allow: [S(YOU, 7)], line: 'Swap it into your last card.' },
  { id: 'lastShot', lesson: 8, kind: 'watch', spot: ['ada'], bot: [D(ADA), S(ADA, 3)], line: 'You’re out. Ada gets one last shot.' },
  { id: 'reveal', lesson: 9, kind: 'say', untilSheet: true, spot: [], line: 'Her hidden card turns over and counts.' },
  { id: 'score', lesson: 9, kind: 'end', spot: [], line: 'Lowest score wins the hole. Well played.' },
];

/** CSS selectors for each spot (all already in the table's DOM). */
export function spotSelector(spot: Spot, me = 0): string {
  if (spot === 'deck') return '[data-coach="deck"]';
  if (spot === 'discard') return '[data-coach="discard"]';
  if (spot === 'held') return '[data-pile="held"]';
  if (spot === 'grid') return '[data-coach="grid"]';
  if (spot === 'ada') return '.tile[data-seat="1"]';
  if (spot.startsWith('slot:')) return `[data-slot-key="${me}:${spot.slice(5)}"]`;
  return `[data-coach="col${spot.slice(4)}"]`;
}
